<template>
  <div class="wrapper">
    <h3 class="title">
      {{ parseTranslation(ADVANCED_SETTINGS) }}
    </h3>

    <div class="advanced-settings">
      <div class="select-profilers">
        <multiselect
          v-if="profilersOptions.length"
          v-model="profilersSelected"
          tag-placeholder="Add this as new tag"
          :placeholder="translations[language][SEARCH_QUOTA]"
          label="label"
          track-by="code"
          :options="profilersOptions"
          :multiple="true"
          :taggable="true"
          @input="profilersValue => updateProfilersSelected(profilersValue)"
        />
      </div>

      <div class="questions">
        <div v-for="(question, index) in questions" :key="question.qid">
          <label> {{ question.question }}:</label>

          <multiselect
            v-model="newQuestions[index].answers"
            tag-placeholder="Add this as new tag"
            :placeholder="translations[language][SEARCH_QUOTA]"
            label="label"
            track-by="code"
            :options="question.answers"
            :multiple="true"
            :taggable="true"
            :closeOnSelect="false"
            @input="
              questionAnswers => updateQuestions(questionAnswers, newQuestions, index)
            "
          />
        </div>
      </div>

      <div v-if="loaderQuestions">
        <Loader />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import Multiselect from 'vue-multiselect'

import Loader from '@/components/loading/Loader-2'

import * as translationAdvancedSettings from './AdvancedSettings.translations'

const { default: translations, ...translationKeys } = translationAdvancedSettings

export default {
  props: {
    survey: {
      required: true,
    },
    country: {
      required: true,
    },
  },

  components: { Loader, Multiselect },

  data() {
    return {
      translations,
      ...translationKeys,
      profilersSelected: null,
      profilersOptions: [],
      profilers: [],
      questions: [],
      newQuestions: [],
      loaderQuestions: false,
    }
  },

  watch: {
    profilersSelected() {
      this.getQuestions()
    },
  },

  methods: {
    ...mapActions([
      'sendMessage',
      'setAnswers',
      'setAdvancedQuestions',
      'executeSurveyAPICall',
    ]),

    parseProfileOptions(profiles) {
      function changeSelPosition(array) {
        const selIndexPosition = 9
        const removeCount = 1
        const selElement = array.splice(selIndexPosition, removeCount)

        return [...selElement, ...array]
      }

      this.profilersOptions = changeSelPosition(
        profiles.map(profiler => {
          return {
            code: profiler.code,
            label: profiler.name,
          }
        }),
      )
    },

    updateProfilersSelected(selected) {
      this.setAnswers(selected)
      this.profilersSelected = selected
    },

    updateQuestions(questionAnswers, newQuestions, index) {
      const copyNewQuestions = [...newQuestions.map(question => ({ ...question }))]
      this.setAdvancedQuestions(copyNewQuestions)
      newQuestions[index].answers = questionAnswers
    },

    async getProfilers() {
      const options = await this.executeSurveyAPICall({
        path: 'sample/options',
      })

      if (!options) return

      this.profilers = options.profilers

      this.parseProfileOptions(options.profilers)
    },

    async getQuestions() {
      if (!this.profilersSelected.length) return (this.questions = [])

      const profilers = await this.executeSurveyAPICall({
        path: 'sample/profilers',
        method: 'POST',
        payload: {
          country: this.country.value,
          language: this.survey.lang,
          profilers: this.profilersSelected.map(profiler => profiler.label),
        },
      })

      if (!profilers) return

      this.questions = profilers.questions

      const oldQuestions = this.newQuestions

      this.newQuestions = this.questions.map(question => {
        const [firstQuestion] = oldQuestions.filter(
          oldQuestion => oldQuestion.qid === question.qid,
        )

        return {
          ...question,
          answers: firstQuestion?.answers || [],
        }
      })
    },

    init() {
      const sampleApi = this.survey.questions_groups.filter(
        group => group.group_code === 'Sample',
      )
      if (sampleApi.length) {
        const profilers = sampleApi[0].questions.filter(
          filter =>
            filter.code !== 'ows-2' &&
            filter.code !== 'ows-1' &&
            filter.code !== 'ows-18' &&
            filter.code !== 'ows-16' &&
            filter.code !== 'ows-17' &&
            filter.code !== 'ows-19',
        )

        this.newQuestions = profilers

        const arrayProfilers = profilers.map(profiler => profiler.code.split('-')[1])

        this.profilersSelected = this.profilersOptions.filter(profiler =>
          arrayProfilers.includes(profiler.code),
        )
      }
    },
  },

  async created() {
    await this.getProfilers()
    this.init()
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  .title {
    display: flex;
    align-items: center;
    @include tipography(16);
    color: $white;
    font-weight: bold;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    white-space: nowrap;
    margin-bottom: 20px;
    margin-top: 20px;
    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background: $white;
      margin-left: 15px;
    }
  }

  .select-profilers {
    position: relative;
  }

  .choose-dropdown {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid $gray-5;
    padding: 5px 10px;
    width: 100%;
    @include tipography(16);
    color: $gray-2;
    p {
      display: flex;
      flex-wrap: wrap;
    }
  }

  .profiler-pill {
    display: flex;
    align-items: center;
    background: $gray-5;
    padding: 0px 6px 0px 10px;
    border-radius: 30px;
    margin: 5px;
    @include tipography(12);
    color: $gray-2;
    button {
      position: relative;
      top: 2px;
      border: none;
      background: none;
      padding: 5px;
      cursor: pointer;
      &:focus {
        outline: none;
      }
      img {
        height: 12px;
      }
    }
  }

  .questions {
    position: relative;
    width: 100%;
    margin-top: 20px;
    > div {
      padding-bottom: 10px;
      label {
        display: inline-block;
        width: 100%;
        @include tipography(14);
        margin-right: 10px;
        color: $white;
        line-height: 1.2rem;
        margin-bottom: 10px;
      }
    }
  }

  .light & {
    .title {
      color: $main;

      &::after {
        background: $gray-5;
      }
    }

    .questions {
      > div {
        label {
          color: $gray-2;
        }
      }
    }
  }
}
</style>
